<template>
  <Section
      v-bind="{
        ...sectionProps,
        class: 'priceshow-category__section'
      }"
  >
    <div>
      <div class="container-fluid">
        <div class="priceshow-category__section__header">
          <div
              class="priceshow-category__section__header__headline"
              :class="{
                'is-action': isMobileBreakpoint
              }"
              @click="showCalculator"
          >
            Deine Angaben
            <FontAwesomeIcon
                v-if="isMobileBreakpoint"
                :icon="faPencil"
            />
          </div>
          <div>
            <Expansion :expanded="!isMobileBreakpoint || calculatorExpanded">
              <BaseCalculator
                  :branches="modelValue?.branch"
                  :options="{
                    branch: branch.slug,
                    productId: modelValue?.productId,
                    postcode: modelValue?.postcode,
                    city: modelValue?.city,
                    street: modelValue?.street,
                    houseNumber: modelValue?.houseNumber,
                    usage: modelValue?.usage,
                    business: modelValue?.customerType === 'business',
                    isCustomer: !!modelValue?.isCustomer
                  }"
                  wide
                  hide-submit
                  :callback="onCalculatorChange"
              />
            </Expansion>
            <Expansion :expanded="isMobileBreakpoint && !calculatorExpanded">
              <div class="priceshow-category__section__calculator-values">
                <div>Deine Postleitzahl</div>
                <div>{{ modelValue?.postcode || '-' }}</div>
                <div>Deine Stadt</div>
                <div>{{ modelValue?.city || '-' }}</div>
                <div>Deine Straße & Hausnummer</div>
                <div>{{ modelValue?.street || '' }} {{ modelValue?.houseNumber }}</div>
                <template v-if="['power', 'gas'].includes(branch?.mainCategory?.key)">
                  <div>Dein Verbrauch in kWh</div>
                  <div>{{ modelValue?.usage ? formatNumber(modelValue?.usage, 0) : '-' }}</div>
                </template>
                <div>Ich bin Geschäftskund:in</div>
                <div>{{ modelValue?.customerType === 'business' ? 'Ja' : 'Nein' }}</div>
                <template v-if="['internet'].includes(branch?.mainCategory?.key)">
                  <div>Ich bin Strom/Gas Kund:in</div>
                  <div>{{ !!modelValue?.isCustomer ? 'Ja' : 'Nein' }}</div>
                </template>
              </div>
            </Expansion>
          </div>
        </div>
      </div>
    </div>
    <div
        class="priceshow-category__section__content"
        :class="{ loading: priceshow.loading }"
    >
      <div
          v-if="productsSorted.length"
          class="priceshow-category__section__content__slider"
      >
        <CardSlider
            :key="`priceshow-slider-${modelValue?.branch || 'undef'}-${fetchCount}`"
            :branch="branch"
            arrowsActive
        >
          <ProductCard
              v-for="product in productsSorted"
              :key="`fetch-${fetchCount}-product-${product.id}`"
              :is-single="productsSorted.length === 1"
              :product="product"
              @order="onClickOrder"
          />
        </CardSlider>
      </div>
      <div
          v-else-if="!priceshow.loading && fetchCount > 0"
          class="priceshow-category__section__content__notice"
      >
        <div class="container-fluid">
          Leider haben wir an der von dir angegebenen Adresse kein Angebot für dich. Für Fragen stehen wir dir
          jedoch gern zur Verfügung.
        </div>
      </div>
      <BaseLoader
          v-if="priceshow.loading"
          absolute
          no-background
          no-shadow
          color="#fff"
      >
        {{ branch.mainCategory.key === 'internet' ? 'Verfügbarkeit wird geprüft' : 'Deine Preise werden berechnet' }}
      </BaseLoader>
    </div>
    <Teleport
        v-if="branch.mainCategory.key === 'internet'"
        to="body"
    >
      <PriceshowInternetConfigModal
          ref="internetConfigModal"
          :price-params="modelValue"
      />
    </Teleport>
  </Section>
</template>

<script lang="ts" setup>
import BaseCalculator from '@/components/BaseCalculator.vue';
import BaseLoader from '@/components/BaseLoader.vue';
import CardSlider from '@/components/CardSlider.vue';
import Expansion from '@/components/Expansion.vue';
import PriceshowInternetConfigModal from '@/components/order/PriceshowInternetConfigModal.vue';
import Section from '@/components/Section.vue';
import ProductCard from '@/components/product/ProductCard.vue';
import BranchInterface from '@/interfaces/BranchInterface';
import CalculatorModelInterface from '@/interfaces/CalculatorModelInterface';
import PriceshowParamsInterface from '@/interfaces/PriceshowParamsInterface';
import {
  onMounted,
  computed,
  watch,
  ref,
  onBeforeUnmount,
  nextTick,
  inject,
  Ref,
} from 'vue';
import {
  useStore,
} from 'vuex';
import {
  useRoute,
  useRouter,
} from 'vue-router';
import { IoProductAPI } from '@/api';
import Product from '@/classes/Product';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faPencil } from '@fortawesome/pro-solid-svg-icons';
import { formatNumber } from '@/helpers';

library.add(faPencil);

const route = useRoute();
const store = useStore();
const router = useRouter();
const stickyCont = ref();

const props = defineProps<{
  branch: BranchInterface
  sectionProps: Record<string, any>
}>();

const modelValue = defineModel<PriceshowParamsInterface>();
const routeQueryVars = computed(() => route.query);

const priceshow = ref({
  loading: false,
  products: [] as Product[],
});

const isMobileBreakpoint = inject('isMobileBreakpoint') as Ref<boolean>;
const calculatorExpanded = ref<boolean>(!isMobileBreakpoint.value);
const showCalculator = () => {
  calculatorExpanded.value = true;
};

const internetConfigModal = ref<typeof PriceshowInternetConfigModal>();
const productsSorted = ref<Product[]>([]);
const fetchCount = ref(0);

watch(() => priceshow.value.products, () => {
  productsSorted.value = [...priceshow.value.products].sort(($pa, $pb) => {
    if (
      ($pa.isRecommended && !routeQueryVars.value.pid)
        || $pa.id === parseInt(routeQueryVars.value.pid as string, 10)
    ) {
      return -1;
    }

    return $pa.getPriceValue('yearly') < $pb.getPriceValue('yearly') ? 1 : -1;
  });

  fetchCount.value += 1;
});
const fetchPrices = () => {
  priceshow.value.loading = true;

  IoProductAPI.getAllWithPrices({
    ...modelValue.value,
    branch: props.branch.slug,
  }).then((data) => {
    priceshow.value.products = data;
  }).catch(() => {
    priceshow.value.products = [];
  }).finally(() => {
    priceshow.value.loading = false;
  });
};

const calculatorTimeout = ref<any>();
const onCalculatorChange = (formData: CalculatorModelInterface, valid: boolean) => {
  const currentModelValue = { ...modelValue.value } as PriceshowParamsInterface;
  const updatedModelValue: PriceshowParamsInterface = {
    ...currentModelValue,
    customerType: formData.business ? 'business' : 'private',
    isCustomer: !!formData.isCustomer,
    usage: formData.usage as number,
    postcode: formData.postcode as string,
    city: formData.city as string,
    street: formData.street as string,
    houseNumber: formData.houseNumber as string,
  };

  const isDifferentRequest = JSON.stringify(currentModelValue) !== JSON.stringify(updatedModelValue);

  if (valid && isDifferentRequest) {
    if (calculatorTimeout.value) {
      clearTimeout(calculatorTimeout.value);
    }
    calculatorTimeout.value = setTimeout(() => {
      modelValue.value = updatedModelValue;
      calculatorExpanded.value = false;
      nextTick(fetchPrices);
    }, 1500);
  } else if (!valid && calculatorTimeout.value) {
    clearTimeout(calculatorTimeout.value);
  }
};

const showOtherBranchModalProduct = ref<Product | null>(null);

onMounted(() => {
  fetchPrices();
});

const goToOrder = (): void => {
  showOtherBranchModalProduct.value = null;
  priceshow.value.loading = true;
  router.push('/bestellung/adresse/');
};

const cartItemKey = ref(routeQueryVars.value.cik);

const addProductToCart = ($product: Product) => {
  priceshow.value.loading = true;

  store.dispatch('cart/add', {
    product: $product,
    cartItemKey: cartItemKey.value,
    params: modelValue.value,
    entry: true,
  }).then((response) => {
    const responseData = response as {
      cartEnabled: boolean,
    };

    if (responseData.cartEnabled) {
      priceshow.value.loading = false;
      store.commit('cart/SHOW_WIDGET');
    } else {
      goToOrder();
    }
  });
};

const openInternetConfigurationModal = (product: Product) => {
  internetConfigModal.value?.open(product);
};

const onClickOrder = (product: Product) => {
  switch (product.category.key) {
    case 'internet':
      openInternetConfigurationModal(product);
      break;
    default:
      addProductToCart(product);
  }
};

const setStickyTop = () => {
  const header = document.querySelector('header');
  if (stickyCont.value && header) {
    stickyCont.value.style.top = `${header.offsetHeight}px`;
  }
};

onMounted(() => {
  window.addEventListener('resize', setStickyTop);
  window.addEventListener('load', setStickyTop);
  setStickyTop();
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', setStickyTop);
  window.removeEventListener('load', setStickyTop);
});
</script>

<style lang="scss">
@import 'src/scss/component-helpers';

.priceshow-category__section {
  min-height: 67vh;

  .io-section__content {
    display: grid;
    grid-template-columns: 100%;
    gap: 20px;

    @media(min-width: map-get($grid-breakpoints, 'lg')) {
      gap: 60px;
    }
  }

  &__header {
    display: grid;
    gap: 10px;

    @media (min-width: map_get($grid-breakpoints, 'lg')) {
      gap: 20px;
    }

    .calculator .calculator__form {
      margin-top: 0;
    }

    &__headline {
      color: #fff;
      font-size: 1.25rem;
      font-weight: 700;

      @media(min-width: map-get($grid-breakpoints, 'lg')) {
        font-size: 1.875rem;
      }

      &.is-action {
        text-align: center;
        cursor: pointer;

        .fa-pencil {
          font-size: .77778em;
          margin-left: .5em;
        }
      }
    }
  }

  &__calculator-values {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: start;
    gap: 10px;
    color: $white;
    font-size: .875rem;

    > div:nth-child(2n + 1) {
      font-weight: bold;
    }

    > div:nth-child(2n + 2) {
      text-align: end;
    }
  }

  &__content {
    position: relative;

    &.loading {
      min-height: 33.333vh;

      .priceshow-category__section__content__slider,
      .priceshow-category__section__content__notice {
        display: none;
      }
    }

    &__notice {
      color: #fff;
      font-size: 1rem;
      font-weight: 700;
      text-align: center;

      @media (min-width: map_get($grid-breakpoints, 'lg')) {
        font-size: 1.25rem;
        text-align: start;
      }
    }
  }
}
</style>
